import { useEffect, useMemo, useRef, useState } from 'react';
import { DataGridStateService } from '@services/DataGridStateService';
import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { IDataGridStateService } from '@services/IDataGridStateService';
import { IAttachSavedView, fromGridStateSettings } from './attachViews';

export interface IUseAttachViews {
    /** Сырые серверные состояния — формат StateModal (окно «Состояния таблицы»). */
    rawViews: IGridStateSettings[];
    /** Обновление списка из StateModal (onSelectState). */
    setRawViews: (next: IGridStateSettings[] | undefined) => void;
    /** Вид «по умолчанию» — для авто-применения при первой загрузке (или undefined). */
    defaultView: IAttachSavedView | undefined;
    /** Список видов загружен с сервера — момент, когда можно решать про авто-применение. */
    loaded: boolean;
    /** Сервис хранения — StateModal сохраняет/удаляет состояния им же. */
    service: IDataGridStateService;
}

/** Серверное хранилище «Сохранённых видов» (`settings/table/load|save`), keyed по ключу
 *  таблицы — общий, пользовательский слот со старым DevExtreme-гридом, но в формате
 *  AttachGrid. Правки списка делает StateModal (то же окно, что у поисковых папок),
 *  хук лишь держит список и отдаёт разобранные виды. */
export function useAttachViews(tableKey: string, enabled = true): IUseAttachViews {
    const service = useMemo(() => new DataGridStateService(), []);
    const [rawViews, setRawState] = useState<IGridStateSettings[]>([]);
    const [loaded, setLoaded] = useState(false);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        if (!enabled) {
            setRawState([]);
            setLoaded(true);
            return () => {
                alive.current = false;
            };
        }
        setLoaded(false);
        service
            .fetchState(tableKey)
            .then((res) => {
                if (alive.current) setRawState(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                /* нет сохранённых состояний / ошибка сети — оставляем пустой список */
            })
            .finally(() => {
                if (alive.current) setLoaded(true);
            });
        return () => {
            alive.current = false;
        };
    }, [enabled, service, tableKey]);

    const defaultView = useMemo(() => rawViews.map(fromGridStateSettings).find((v) => v.default), [rawViews]);

    return {
        rawViews,
        setRawViews: (next) => setRawState(next ?? []),
        defaultView,
        loaded,
        service,
    };
}
